import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="terms-page" data-testid="terms-page">
      <div className="terms-container">
        {/* Header */}
        <header className="terms-header">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="back-button"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Home
          </Button>
        </header>

        {/* Content */}
        <div className="terms-content">
          <img 
            src="https://customer-assets.emergentagent.com/job_washed-away-cinema/artifacts/zlh4k7vd_LOGO.png" 
            alt="Washed Away - The Beating Heart Behind Australia's Worst Flood"
            className="terms-logo"
          />
          <p className="terms-updated">Terms and Conditions — Online Streaming Service</p>
          <p className="terms-company">Free Flow Films Pty Ltd ACN 666 845 272</p>

          <div className="terms-alert">
            <AlertTriangle className="h-5 w-5" />
            <p>
              These terms and conditions apply to your use of Free Flow Films Pty Ltd ACN 666 845 272 
              ("Free Flow Films", "us", "we") online streaming service. Please read them carefully 
              before accessing or using our online streaming service.
            </p>
          </div>

          <section className="terms-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing or using our online streaming service, you acknowledge that you have read, 
              understood, and agree to be bound by these terms and conditions. If you do not agree to 
              all of the terms and conditions, you may not access or use the service.
            </p>
          </section>

          <section className="terms-section">
            <h2>2. Eligibility and Age Restriction</h2>
            <p>
              You must be at least 15 years of age to access or use our streaming service. If you are 
              under the age of 18, you must have the consent of a parent or legal guardian, who agrees 
              to be bound by these terms and conditions on your behalf. By using the service, you 
              represent and warrant that you meet these eligibility requirements.
            </p>
          </section>

          <section className="terms-section">
            <h2>3. Geographic Restrictions</h2>
            <p>
              Our streaming service is intended for use in Australia only. We make no representation 
              that the content or service is appropriate or available for use in other locations. If 
              you access the service from outside Australia, you do so at your own risk and are 
              responsible for compliance with local laws.
            </p>
          </section>

          <section className="terms-section">
            <h2>4. Content Ownership</h2>
            <p>
              All content streamed through our service, including but not limited to documentaries, 
              films, videos, trademarks, logos, and service marks and accompanying materials, are 
              owned or licensed by Free Flow Films unless otherwise indicated. You may not reproduce, 
              distribute, modify, or create derivative works from any content without prior written 
              consent from Free Flow Films or the relevant third-party rights holder.
            </p>
          </section>

          <section className="terms-section">
            <h2>5. Account Security</h2>
            <p>
              Where access to our streaming service requires an account, you are responsible for 
              maintaining the confidentiality of your login credentials. You agree to notify us 
              immediately at <a href="mailto:support@washedaway.au" className="terms-email-link">support@washedaway.au</a> if 
              you become aware of any unauthorised use of your account. Free Flow Films is not 
              liable for any loss or damage arising from your failure to protect your login credentials.
            </p>
          </section>

          <section className="terms-section">
            <h2>6. Payment and Access Fees</h2>
            <p>
              Where a fee is charged for access to our streaming service, that fee will be specified 
              at the time of purchase. All prices are in Australian dollars and are inclusive of any 
              applicable GST. Fees are non-refundable except where required by Australian Consumer 
              Law or as otherwise stated at the time of purchase.
            </p>
          </section>

          <section className="terms-section">
            <h2>7. Disclaimer of Warranties</h2>
            <p>
              a. Free Flow Films provides the streaming service on an "as is" and "as available" basis. 
              We do not warrant that the service will be uninterrupted, timely, secure, or error-free.
            </p>
            <p>
              b. Content available through our online streaming service is provided for informational 
              and entertainment purposes only. Free Flow Films does not warrant the accuracy, 
              completeness, or reliability of any content streamed through our service. You acknowledge 
              that any reliance on such content is at your own risk.
            </p>
            <p>
              c. Content streamed through our service may contain opinions, viewpoints, and information 
              that may be controversial or inflammatory. Free Flow Films does not endorse any opinions, 
              viewpoints, or information presented in the content.
            </p>
            <p>
              d. Viewers of content streamed through our service are solely responsible for their own 
              reactions and opinions. Free Flow Films shall not be held accountable for any harm or 
              damage that may result from the viewing of such content.
            </p>
          </section>

          <section className="terms-section">
            <h2>8. Limitation of Liability</h2>
            <p>To the maximum extent permitted by law:</p>
            <p>
              a. Free Flow Films shall not be liable for any direct, indirect, incidental, special, 
              consequential, or punitive damages arising out of or in connection with your use of 
              the streaming service; and
            </p>
            <p>
              b. in no event shall Free Flow Films' total aggregate liability to you for all damages 
              exceed the amount actually paid by you for accessing the service in the 12 months 
              preceding the event giving rise to the claim.
            </p>
          </section>

          <section className="terms-section">
            <h2>9. Statutory Conditions (Australian Consumer Law)</h2>
            <p>
              Certain legislation, including the Australian Consumer Law (Schedule 2 of the Competition 
              and Consumer Act 2010 (Cth)), may imply warranties or conditions or impose obligations 
              which cannot be excluded, restricted, or modified except to a limited extent. These terms 
              and conditions must be read subject to those statutory provisions. To the extent we are 
              permitted to do so, we limit our liability in respect of any claim to, at our option:
            </p>
            <p>a. in relation to services:</p>
            <ul>
              <li>(i) the supply of the services again; or</li>
              <li>(ii) the payment of the cost of having the services supplied again; and</li>
            </ul>
            <p>b. in relation to goods:</p>
            <ul>
              <li>(i) the replacement of the goods or the supply of equivalent goods;</li>
              <li>(ii) the repair of the goods;</li>
              <li>(iii) the payment of the cost of replacing the goods or acquiring equivalent goods; or</li>
              <li>(iv) the payment of the cost of having the goods repaired.</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>10. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Free Flow Films and its affiliates, 
              directors, officers, employees, and agents from and against any and all claims, 
              liabilities, damages, losses, costs, expenses, or fees (including reasonable legal fees) 
              arising out of or in connection with your use of our streaming service or your breach 
              of these terms and conditions.
            </p>
          </section>

          <section className="terms-section">
            <h2>11. Changes to Terms</h2>
            <p>
              Free Flow Films reserves the right to modify or replace these terms and conditions at 
              any time. We will notify you of any material changes by email to the address associated 
              with your account, or by a prominent notice on our website, at least 14 days before the 
              changes take effect. Your continued use of the streaming service after the effective 
              date of any such changes constitutes your acceptance of the new terms and conditions.
            </p>
          </section>

          <section className="terms-section">
            <h2>12. Privacy Policy</h2>
            <p>
              a. Free Flow Films respects your privacy and is committed to protecting your personal 
              information in accordance with the Privacy Act 1988 (Cth) and the Australian Privacy 
              Principles. This clause outlines how we collect, use, and disclose information when 
              you access or use our streaming service. By accessing or using the service, you agree 
              to the terms of this Privacy Policy.
            </p>
            <p>
              b. We may collect personal information from you when you register for an account, make 
              a purchase, or otherwise interact with our streaming service. This may include your 
              name, email address, billing information, and other details necessary to provide you 
              with access to the service.
            </p>
            <p>
              c. We may also collect non-personal information such as your IP address, device 
              information, and browsing behaviour to analyse trends, administer the service, and 
              improve our offerings.
            </p>
            <p>
              d. Free Flow Films will not sell, rent, or lease your personal information to third 
              parties without your consent, except as required by law, as necessary to provide the 
              streaming service, or as otherwise permitted under the Privacy Act 1988 (Cth).
            </p>
            <p>
              e. We store your personal information securely on servers located in Australia. We 
              take reasonable steps to protect your information from misuse, interference, loss, 
              and unauthorised access, modification, or disclosure.
            </p>
            <p>
              f. You have the right to request access to, or correction of, your personal information 
              held by us. To make such a request, or if you have any questions or concerns about our 
              Privacy Policy, please contact us at <a href="mailto:support@washedaway.au" className="terms-email-link">support@washedaway.au</a>.
            </p>
            <p>
              g. By using our streaming service, you consent to the collection, processing, and 
              storage of your personal information in accordance with this Privacy Policy.
            </p>
          </section>

          <section className="terms-section">
            <h2>13. User Conduct</h2>
            <p>
              a. You agree to use the streaming service in compliance with all applicable laws, 
              regulations, and third-party agreements.
            </p>
            <p>
              b. You must not engage in any conduct that may disrupt or interfere with the 
              functioning of the streaming service or infringe upon the rights of others.
            </p>
            <p>
              c. Free Flow Films reserves the right to suspend or terminate access to the streaming 
              service for users who violate these terms and conditions or engage in prohibited conduct.
            </p>
          </section>

          <section className="terms-section">
            <h2>14. Termination of Service</h2>
            <p>
              Free Flow Films reserves the right to suspend or terminate the streaming service or 
              any part thereof at any time without prior notice. In the event of termination, to the 
              maximum extent permitted by law, Free Flow Films shall not be liable to you or third 
              parties for any damages or losses incurred.
            </p>
          </section>

          <section className="terms-section">
            <h2>15. Dispute Resolution</h2>
            <p>
              a. In the event of a dispute arising out of or relating to these terms and conditions 
              or the use of the streaming service, the parties agree to first attempt to resolve 
              the dispute through good faith negotiation.
            </p>
            <p>
              b. If the dispute cannot be resolved through negotiation within 30 days, either party 
              may refer the matter to mediation administered by the Australian Disputes Centre or 
              a mutually agreed mediator.
            </p>
            <p>
              c. If mediation is unsuccessful, either party may pursue their rights through the 
              courts in accordance with clause 18 (Governing Law) below.
            </p>
          </section>

          <section className="terms-section">
            <h2>16. Entire Agreement</h2>
            <p>
              These terms and conditions, together with any additional policies or agreements 
              referenced herein, constitute the entire agreement between the parties regarding 
              the use of the streaming service and supersede all prior agreements and understandings, 
              whether written or oral.
            </p>
          </section>

          <section className="terms-section">
            <h2>17. Waiver</h2>
            <p>
              The failure of Free Flow Films to enforce any provision of these terms and conditions 
              shall not constitute a waiver of that provision or any other provision herein.
            </p>
          </section>

          <section className="terms-section">
            <h2>18. Governing Law</h2>
            <p>
              These terms and conditions shall be governed by and construed in accordance with the 
              laws of the State of New South Wales, Australia. You consent to the non-exclusive 
              jurisdiction of the courts of that State.
            </p>
          </section>

          <section className="terms-section">
            <h2>19. Contact Us</h2>
            <p>If you have any questions about these terms and conditions, please contact us at:</p>
            <div className="contact-info">
              <p><strong>Free Flow Films Pty Ltd</strong></p>
              <p>ACN 666 845 272</p>
              <p>Email: <a href="mailto:support@washedaway.au" className="terms-email-link">support@washedaway.au</a></p>
            </div>
          </section>

          <div className="terms-footer">
            <p className="terms-last-updated">Last updated: February 2026</p>
            <p>
              By clicking "Pay" during checkout, you acknowledge that you have read, 
              understood, and agree to be bound by these Terms & Conditions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
